import * as THREE from 'three';
import { createHealth, applyDamage, tickHealth, FLASH_DURATION, type Health } from '../combat/damage';
import type { AimTarget } from '../combat/autoaim';
import type { BlockPhysics } from '../world/collision';
import { CHARACTER_FORWARD_YAW_OFFSET } from '../core/assets';

/**
 * S06's boss framework — a deliberately SEPARATE class from `ai/enemy.ts`'s
 * `Enemy`, not a new archetype/movement-mode on it. A boss needs three
 * things no roster enemy does: TWO independently-telegraphed attacks
 * cycling in a fixed pattern (Enemy's shared telegraph/attack resolution
 * only models one), an EXPOSED punish window with its own damage multiplier
 * (PLAN-CITY §5: "exposed with 1.5x exMul" — Classic's own boss pattern,
 * never a roster-enemy concept), and no leashing/chase-idle/nav-pathing at
 * all (a boss stands in its arena; PLAN-CITY §5's own "arena gating" is a
 * BossArena concern, not something the boss's own FSM needs to know about).
 * Reusing `combat/damage.ts`'s `Health`/`applyDamage`/`tickHealth` keeps the
 * §0.7 single-damage-choke-point discipline intact even though the FSM
 * itself is bespoke — see `combat/bossResolve.ts` for the parallel (not
 * shared) damage-IN resolution functions this split requires.
 *
 * PLAN-CITY3 §4 K-1 ("Gridlock 2.0", L1 EARNED WINDOWS): the fight used to
 * grant EXPOSED unconditionally after every attack, which made the fight
 * "stand there and it dies" (no gate at all). Now the boss is ARMORED BY
 * DEFAULT — `takeDamage()` applies CHIP_DAMAGE_MULTIPLIER (10%) outside
 * EXPOSED — and EXPOSED is earned only by BREAKING an attack via a genuine
 * positive action (standing on a wreck deck at the shockwave's impact tick,
 * or shooting the hubcap down mid-flight). An un-broken attack recovers
 * straight to `idle` — no gold tint, no damage bonus. Three consecutive
 * un-broken cycles arm an ENRAGE (a heavy, pocket-wide reuse of the
 * shockwave, survivable-but-slow the first time an un-answered enrage
 * lands, lethal-tier the second — PLAN-CITY2 §0.5's law, ported from
 * RatKingBoss's RED CHARGE idiom for "no new clip exists, tint + HUD cue
 * carry the tell").
 */
export type BossState = 'idle' | 'telegraph1' | 'attack1' | 'telegraph2' | 'attack2' | 'attack2_wait' | 'exposed' | 'dead';

// --- Gridlock tuning ---
// maxHp was a FIRST-PASS ESTIMATE against the OLD unconditional-exposed
// design. K-1(f) re-derives it against the NEW earned-window economy by
// MEASURING, not re-guessing arithmetic: `tools/test-gridlock-fsm.ts`'s
// perfect-play driver (every shockwave/hubcap broken, continuous ~58.9
// dmg/s auto-aim fire — The Nine's 100%-uptime rate, the same reference
// weapon the old estimate used) runs the REAL `Boss` FSM + `takeDamage`
// chip/exposed multiplier end to end and reports actual time-to-kill. At
// the old 1500 HP that measured 31.2s — well under PLAN-CITY §5's 45-100s
// band, because outside EXPOSED the player now deals only 10% damage
// (CHIP_DAMAGE_MULTIPLIER), so the old HP was tuned for a damage economy
// that no longer exists. 3400 measures at ~70s, comfortably mid-band — see
// that file's own S1 assertion for the up-to-date number if this is ever
// retuned again. Flag to Karan at the first real den playtest; a scripted
// bot's break-timing is not a substitute for real human reaction time.
export const GRIDLOCK_MAX_HP = 3400;
// PLAN-CITY §5's literal spec — not tunable without a plan-doc edit.
export const EXPOSED_DAMAGE_MULTIPLIER = 1.5;
/** K-1(a): "armored by default" — every hit outside EXPOSED deals only this fraction of its nominal damage. Applied in `takeDamage()`, the single choke point (§0.7), so every damage path (bullets/melee/super) gets it identically. */
export const CHIP_DAMAGE_MULTIPLIER = 0.1;

const ENGAGE_DELAY = 0.6; // beat between the HP bar appearing and the first telegraph, so the intro reads before the fight starts
const EXPOSED_DURATION = 2.2; // the punish window's length — a first-pass estimate, same caveat as GRIDLOCK_MAX_HP
const HIT_FLASH_CLIP_DURATION = 0.333; // matches the delivered `hit` clip (report.json: 9 frames @ 24fps)
const DEATH_FADE_DURATION = 1.0; // matches the delivered `death` clip's own duration
const WHITE = new THREE.Color(0xffffff);
const GOLD = new THREE.Color(0xffc94a); // Classic's own EXPOSED gold (blast-runner.html TOKENS.taxiGold), reused here for visual-language consistency across the two games
/** K-1(b): the enrage's own tint — same red `ai/ratking.ts`'s RED CHARGE uses, for visual-language consistency between the two bosses' "this is dangerous, not a punish window" cue. */
const RED = new THREE.Color(0xff3b30);

// attack1 (shockwave) — a stationary ground-slam AoE centered on the boss.
// Radius is deliberately SMALLER than GRIDLOCK_ARENA's own 5.5m (world/
// arena.ts) so there's real room to dodge to the arena's rim rather than the
// attack blanketing the whole fight space.
export const SHOCKWAVE_RADIUS = 4;
export const SHOCKWAVE_DAMAGE = 24;
/**
 * K-1(b) enrage — reuses the shockwave's own attack1/telegraph1 clips (no
 * new animation exists) at pocket-wide radius so the wreck decks' on-deck
 * break flag, not distance, is what makes the wrecks the position play
 * (world/arena.ts's own K-3 comment already states the three deck-distance
 * numbers this radius is deliberately wider than). FIRST tier is
 * survivable-but-slow (PLAN-CITY2 §0.5); LETHAL is the SECOND consecutive
 * un-answered enrage's wipe.
 */
export const ENRAGE_SHOCKWAVE_RADIUS = 9;
export const ENRAGE_SHOCKWAVE_DAMAGE_FIRST = 40;
export const ENRAGE_SHOCKWAVE_DAMAGE_LETHAL = 999; // guarantees a kill via applyDamage's own HP floor-at-0 clamp — this IS "the wipe," not a survivable number
/** K-1(b): how many consecutive un-broken NORMAL cycles arm an enrage attempt. */
const ENRAGE_ARM_CYCLES = 3;
/** K-1(b): the enrage only fires while boss HP sits in this fraction band — PLAN-CITY3 §4's own literal "60-70% of max HP" spec. */
const ENRAGE_HP_BAND_MIN = 0.6;
const ENRAGE_HP_BAND_MAX = 0.7;
// attack2 (hubcap_release) — a thrown arcing projectile, reusing
// combat/resolve.ts's existing `computeLobVelocity` (the same ballistic
// solve the trash-can lobber already uses) and the shared enemy-owned
// ProjectilePool, so damage-to-player resolution is free via the EXISTING,
// already-reviewed `resolveProjectileDamageOnPlayer` (main.ts just has to
// fire into the pool with owner:'enemy' — no new player-damage code needed).
export const HUBCAP_DAMAGE = 20;
export const HUBCAP_SPEED = 11;
export const HUBCAP_RADIUS = 0.3; // bigger than the trash-can lobber's 0.2 — a hubcap reads as a larger flying object
export const HUBCAP_COLOR = 0xb8bcc4;
/** K-1(a): "hubcap broken by shooting it down in flight" — a small HP pool a player's own bullets can deplete before the hubcap ever reaches the player. Roughly one solid hit (The Nine's ~13 avg dmg) or a couple pellets — deliberately easy to hit once you know to try, matching "boss difficulty = knowledge, not reflexes." */
export const HUBCAP_BREAK_HP = 15;
/**
 * K-1(a): once attack2's own clip ends, the FSM waits (in `attack2_wait`)
 * for the fired hubcap to resolve (hit the player, expire, or get shot
 * down) before deciding broken-vs-unbroken — this safety ceiling matches
 * the hubcap's own `life:3` (main.ts's fire() call) plus travel-time margin,
 * same "belt and suspenders" convention as ai/ratking.ts's SWARM_WAIT_MAX.
 */
const ATTACK2_WAIT_MAX = 3.5;
// Review finding: the hubcap's ballistic arc used to be solved for a 0.9m
// launch height (combat/resolve.ts's computeLobVelocity, shared with the
// trash-can lobber) while actually FIRING from 1.6m — the two numbers lived
// in two different places (boss.ts's own tuning + a bare literal in
// main.ts's fire() call) and silently drifted apart, a real instance of
// gotcha #19's "one set of numbers, not two" lesson. Now the single source
// of truth for BOTH the velocity solve and the actual fire origin — see
// main.ts's hubcap-fire block, which passes this to both.
export const HUBCAP_LAUNCH_HEIGHT = 1.6;
// Review finding: bossResolve.ts's three damage-in functions each hardcoded
// their own `1.2` "rough body radius" literal for engagement-range checks —
// exported here as the one source of truth, and now ALSO the real radius
// `applyKnockbackMovement` resolves knockback against (previously nothing
// did — see that method's own comment).
export const BOSS_RADIUS = 1.2;
// Gridlock's own real height per C05's delivery (report.json: 2.65m
// grounded) — used as the head-height bound for knockback wall collision,
// matching how Enemy's own applyKnockbackMovement uses its archetype's
// real height rather than a guessed constant.
const BOSS_HEIGHT = 2.65;
/** K-1(c) "physicality": incoming knockback impulse is reduced by this fraction — today's un-resisted knockback pushed Gridlock ~1m/s into its own fence under sustained fire, out of its own attack range. 0.9 = only 10% of the impulse actually moves the boss. */
export const GRIDLOCK_KNOCKBACK_RESIST = 0.9;
/** K-1(c) "home leash": knockback drift is hard-clamped to this radius from the boss's own spawn/home point every tick, so sustained fire can never push it meters away from its pocket regardless of how many hits land before the resist above decays them. */
export const GRIDLOCK_HOME_LEASH = 1.5;

// Attack1 (shockwave — a ground slam AoE) deals its hit at a fixed point
// INTO the attack1 clip, not at state-entry (frame 0) or state-exit — frame
// 0 would read as "damage before the slam even starts swinging," which is
// unfair-feeling, and Classic's own §0.7 law ("every telegraph is
// mechanics") extends naturally to "the ATTACK itself should look like it's
// landing when it lands." 0.3s into a 0.625s clip is roughly the midpoint,
// approximating where a downward slam's impact frame would sit.
//
// K-1(b)'s own real warning numbers, restated here since PLAN-CITY3 §4
// names them explicitly and this is where they're derived: 0.925s from
// telegraph1's own clip start to the shockwave's impact (telegraph1's
// ~0.625s duration + this 0.3s) — the enrage reuses this exact timing,
// distinguished only by tint/radius/damage, never a new clip.
const ATTACK1_IMPACT_T = 0.3;
// Attack2 (hubcap_release) fires at state entry (t=0) — a "release" is
// naturally an instantaneous launch moment, unlike a slam's brief
// travel-to-impact, so there's no equivalent mid-clip delay to model.
const ATTACK2_RELEASE_T = 0;

export interface BossVisual {
  group: THREE.Group;
  materials: THREE.Material[];
  mixer: THREE.AnimationMixer | null;
  clips: Map<string, THREE.AnimationClip> | null;
}

export interface BossTickResult {
  /** This tick is attack1's (or the enrage's) impact frame — the manager should resolve the shockwave AoE against the player using `shockwaveRadius`/`shockwaveDamage` below, NOT the fixed SHOCKWAVE_RADIUS/SHOCKWAVE_DAMAGE constants (which are only the NORMAL-tier defaults an enrage overrides). */
  shockwaveFired: boolean;
  /** Valid only when `shockwaveFired` — SHOCKWAVE_RADIUS for a normal shockwave, ENRAGE_SHOCKWAVE_RADIUS for an enrage. */
  shockwaveRadius: number;
  /** Valid only when `shockwaveFired` — SHOCKWAVE_DAMAGE normally; ENRAGE_SHOCKWAVE_DAMAGE_FIRST/_LETHAL during an enrage (the caller still must gate actual damage on whether the hit was broken — see K-1(a)'s comment on `updateAttack1`). */
  shockwaveDamage: number;
  /** This tick is attack2's release frame — the manager should fire a hubcap projectile toward the captured target. */
  projectileToFire: { origin: THREE.Vector3; targetPos: THREE.Vector3 } | null;
}

export class Boss {
  readonly position = new THREE.Vector3();
  readonly health: Health;
  readonly group: THREE.Group;
  state: BossState = 'idle';

  private readonly visual: BossVisual;
  private readonly baseEmissive = new Map<THREE.MeshStandardMaterial, THREE.Color>();
  private stateT = 0;
  private deathT = 0;
  private hitFlashClipT = 0;
  private currentClip = '';
  private facing = 0;
  /** Which attack to open with next — alternates 1,2,1,2... every cycle. Deliberately deterministic (not randomized): a fixed, learnable pattern is fairer for a first-pass boss fight, and keeps harness verification exact rather than probabilistic. Untouched by the enrage, which is a THIRD, orthogonal cycle layered on top (see `enrageActive`) rather than a branch of this alternation. */
  private nextAttack: 1 | 2 = 1;
  private hasFiredThisAttack = false;
  /** K-1(a): whether the CURRENT attack (normal or enrage) has been broken by a positive action — set synchronously at the shockwave's impact tick (on-deck or slide-invulnerable), or asynchronously once the hubcap resolves (`reportHubcapBroken()`). Reset to `false` on entering attack1/attack2. */
  private currentAttackBroken = false;
  /** K-1(b): true while the current telegraph1/attack1 cycle is the heavy pocket-wide enrage variant rather than a normal shockwave — same clips, different radius/damage/tint, per this file's own header comment. */
  private enrageActive = false;
  /** K-1(b): consecutive NORMAL (non-enrage) cycles that resolved un-broken — armed to 0 the moment any cycle (normal or enrage) IS broken; reaching ENRAGE_ARM_CYCLES while HP sits in the enrage HP band triggers the next opener as an enrage instead of the normal alternation. */
  private consecutiveUnbrokenCycles = 0;
  /** K-1(b): consecutive UN-ANSWERED enrages — reset to 0 the moment an enrage IS broken; reaching 2 (i.e. this is about to be the SECOND in a row) selects the LETHAL damage tier instead of the survivable-but-slow FIRST tier, per PLAN-CITY2 §0.5. */
  private unansweredEnrageStreak = 0;
  // S08 spec-fidelity review finding, same class as Enemy.aimTarget's own
  // fix (ai/enemy.ts): `position` is always just a live reference to
  // `this.position`, so only `isTargetable` needs updating per access —
  // one cached instance field instead of a fresh wrapper object on every
  // read (up to once per tick, from resolveAimDirection's own aimTargets
  // build in main.ts).
  private readonly cachedAimTarget: AimTarget = { position: this.position, isTargetable: false };
  // Debug investigation finding (chasing an intermittent "super blast
  // damages an engaged boss" harness FAIL): `applyKnockbackMovement` (below)
  // is this session's own new capability — before it, `position` was set
  // once here and NEVER touched again, so nothing could ever move it and a
  // "reset the boss's position" concern simply didn't exist yet. Now that
  // real combat can drag the boss meters away from its own arena center,
  // `resetEncounter()` — this codebase's designed general-purpose re-fight
  // mechanism, per its own header comment, and exactly what S10's planned
  // "repeatable Gridlock lair" will call between fights — needs a real home
  // to snap back to. Stored here (not re-read from `GRIDLOCK_ARENA` in
  // world/arena.ts) so `Boss` stays fully self-contained and agnostic of
  // which arena owns it, matching how it already takes spawnX/spawnZ as
  // plain constructor params rather than importing the arena constant itself.
  private readonly homeX: number;
  private readonly homeZ: number;

  constructor(visual: BossVisual, spawnX: number, spawnZ: number) {
    this.visual = visual;
    this.group = visual.group;
    this.health = createHealth(GRIDLOCK_MAX_HP, 0);
    this.homeX = spawnX;
    this.homeZ = spawnZ;
    this.position.set(spawnX, 0, spawnZ);
    for (const m of visual.materials) {
      if (m instanceof THREE.MeshStandardMaterial) this.baseEmissive.set(m, m.emissive.clone());
    }
    // Review finding: ATTACK1_IMPACT_T is a hardcoded assumption about
    // attack1's own clip duration (valid for the delivered Gridlock GLB,
    // per its own comment) with nothing enforcing the relationship — a
    // future re-timed clip shorter than this would silently skip the
    // shockwave's damage instance every cycle, with no error anywhere (the
    // byte-level validator checks clip presence/naming, not this
    // cross-constant timing). Same invariant-documentation pattern as
    // combat/autoaim.ts's AIM_RANGE-vs-aggroRange comment, checked here
    // (not just commented) since the real clip duration is already known
    // once the GLB has loaded.
    const attack1Duration = visual.clips?.get('attack1')?.duration;
    if (attack1Duration !== undefined && attack1Duration <= ATTACK1_IMPACT_T) {
      console.warn(`[Boss] attack1 clip duration (${attack1Duration}s) is <= ATTACK1_IMPACT_T (${ATTACK1_IMPACT_T}s) — the shockwave will never fire. Lower ATTACK1_IMPACT_T to match.`);
    }
    this.syncTransform();
    this.playClip('idle', true);
  }

  get aimTarget(): AimTarget {
    this.cachedAimTarget.isTargetable = this.state !== 'dead';
    return this.cachedAimTarget;
  }

  get isExposed(): boolean {
    return this.state === 'exposed';
  }

  /** K-1(b): whether the boss is currently mid-enrage (telegraph1/attack1 with `enrageActive`) — main.ts uses this to decide the HUD cue and to gate the shockwave-tier damage it applies. */
  get isEnraging(): boolean {
    return this.enrageActive && (this.state === 'telegraph1' || this.state === 'attack1');
  }

  /**
   * Called once, the exact tick `BossArena` transitions dormant->active —
   * arms the ENGAGE_DELAY beat before the first telegraph, so the HP bar/
   * intro toast have a moment to register before the fight actually starts.
   * A no-op if the boss isn't in `idle` (e.g. a stray double-call, or the
   * arena somehow activates while a resumed/left-mid-fight boss is already
   * mid-attack) — only `idle`'s own timer means anything here.
   */
  engage(): void {
    if (this.state === 'idle') this.stateT = ENGAGE_DELAY;
  }

  /** Restores a mid-fight retreat back to a fresh encounter (BossArena's reset-on-leave rule) — full HP, back to idle, next attack resets to 1 so a reset fight always opens the same way. K-1(b): also clears the enrage-escalation counters — a retreat is a full disengage, not a "the player is still learning this cycle" moment. */
  resetEncounter(): void {
    this.health.hp = this.health.maxHp;
    this.health.invulnerableT = 0;
    this.health.flashT = 0;
    this.health.knockbackVel.set(0, 0, 0);
    this.state = 'idle';
    this.stateT = 0;
    this.hitFlashClipT = 0;
    this.nextAttack = 1;
    this.hasFiredThisAttack = false;
    this.currentAttackBroken = false;
    this.enrageActive = false;
    this.consecutiveUnbrokenCycles = 0;
    this.unansweredEnrageStreak = 0;
    this.currentClip = '';
    // See homeX/homeZ's own field comment: knockback can drag the boss away
    // from its arena center over a fight, and nothing else ever brings it
    // back — a reset that restores full HP but leaves the boss stranded
    // outside its own engagement radius is not actually a fresh encounter.
    this.position.set(this.homeX, 0, this.homeZ);
    this.facing = 0;
    this.syncTransform();
    // Review finding: the dead-state death-fade (update()'s own dead
    // branch) shrinks group.scale toward 0 over DEATH_FADE_DURATION —
    // unreachable in TODAY's production code (main.ts only calls this while
    // `boss.state !== 'dead'`), but PLAN-CITY §5's own Wave-C S10 line plans
    // a "repeatable Gridlock lair," and this method is the codebase's
    // designed general-purpose re-fight mechanism (world/arena.ts's own
    // comment says as much) — the obvious way a future rematch feature
    // reaches for it. Without this, a boss reset after any death would
    // render permanently shrunk for the entire rematch.
    this.group.scale.setScalar(1);
    this.playClip('idle', true);
  }

  /**
   * K-1(a): "armored by default" — outside EXPOSED, incoming damage is
   * scaled to CHIP_DAMAGE_MULTIPLIER (10%); during EXPOSED it's the existing
   * EXPOSED_DAMAGE_MULTIPLIER (1.5x) bonus, unchanged. Returns true if this
   * hit was lethal. Routes through the shared `applyDamage` choke point
   * (§0.7) — never mutates `.health.hp` itself.
   */
  takeDamage(amount: number, knockbackDir: THREE.Vector3, knockbackForce: number): boolean {
    if (this.state === 'dead') return false;
    const applied = this.isExposed ? amount * EXPOSED_DAMAGE_MULTIPLIER : amount * CHIP_DAMAGE_MULTIPLIER;
    const died = applyDamage(this.health, applied, { knockbackDir, knockbackForce });
    if (died) {
      this.state = 'dead';
      this.deathT = DEATH_FADE_DURATION;
      this.currentClip = '';
      this.playClip('death', false);
    } else if (this.state === 'idle' || this.state === 'exposed') {
      // A "hit" flinch is purely cosmetic and only ever plays during safe
      // windows (idle/exposed) — NEVER during telegraph1/attack1/telegraph2/
      // attack2/attack2_wait, where interrupting the clip would break the
      // readable telegraph->attack contract §0.7 requires. The boss's own
      // FSM `state`/`stateT` are untouched either way; this only overrides
      // which clip plays for a moment.
      this.hitFlashClipT = HIT_FLASH_CLIP_DURATION;
      this.currentClip = '';
      this.playClip('hit', false);
    }
    return died;
  }

  /** K-1(a): called by main.ts SYNCHRONOUSLY, in the same tick as `BossTickResult.shockwaveFired`, once it has determined whether the player was on a wreck deck or slide-invulnerable at the exact impact moment — see `updateAttack1`'s own comment for why this is safe to call synchronously (the exit transition to exposed/idle only runs on a LATER tick). */
  reportShockwaveBroken(): void {
    this.currentAttackBroken = true;
  }

  /** K-1(a): called by main.ts once it determines the fired hubcap was destroyed by player fire before resolving naturally — may land on a LATER tick than attack2's own clip end (the hubcap has real travel time), which is exactly why `attack2_wait` exists to hold the FSM open for it. */
  reportHubcapBroken(): void {
    this.currentAttackBroken = true;
  }

  private playClip(name: string, loop: boolean): void {
    if (!this.visual.mixer || !this.visual.clips) return;
    if (this.currentClip === name) return;
    const clip = this.visual.clips.get(name);
    if (!clip) return;
    this.visual.mixer.stopAllAction();
    const action = this.visual.mixer.clipAction(clip);
    action.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, Infinity);
    action.clampWhenFinished = !loop;
    action.reset().play();
    this.currentClip = name;
  }

  /**
   * `encounterActive` is BossArena's own state, fed in every tick rather
   * than cached — a dormant boss just idles (spoke rotation, per C05's own
   * NOTES.md: "that constant motion is this boss's alive-ness") and never
   * advances its attack FSM, regardless of health/hitstun, until the arena
   * says the player has actually crossed the entry threshold.
   *
   * K-1: `playerOnWreckDeck`/`playerSlideInvulnerable` are continuous,
   * caller-computed facts about the player's CURRENT position/state (not
   * one-shot events) — read only at the exact shockwave impact tick, the
   * same fire-time-capture discipline `playerPos` itself already gets for
   * the hubcap's target. `hubcapInFlight` mirrors RatKingBoss's own
   * `addsAlive` pattern: true while a previously-fired hubcap is still
   * `.active` in main.ts's projectile pool, read only while `attack2_wait`
   * cares about it.
   */
  update(
    dt: number,
    playerPos: THREE.Vector3,
    encounterActive: boolean,
    physics: BlockPhysics,
    playerOnWreckDeck: boolean,
    playerSlideInvulnerable: boolean,
    hubcapInFlight: boolean,
  ): BossTickResult {
    const result: BossTickResult = { shockwaveFired: false, shockwaveRadius: SHOCKWAVE_RADIUS, shockwaveDamage: SHOCKWAVE_DAMAGE, projectileToFire: null };
    tickHealth(this.health, dt);
    this.applyKnockbackMovement(dt, physics);
    this.visual.mixer?.update(dt);

    if (this.state === 'dead') {
      this.deathT -= dt;
      const t = Math.max(0, this.deathT / DEATH_FADE_DURATION);
      this.group.scale.setScalar(t); // fades by scaling toward 0, same idiom as Enemy's own death-fade
      this.syncTransform();
      return result;
    }

    if (this.hitFlashClipT > 0) {
      this.hitFlashClipT -= dt;
      if (this.hitFlashClipT <= 0) this.currentClip = ''; // force the next playClip call (idle/exposed's own loop) to actually re-trigger, since currentClip still reads 'hit' otherwise
    }

    if (!encounterActive) {
      if (this.hitFlashClipT <= 0) this.playClip('idle', true);
      this.applyMechanicTint();
      this.applyHitFlashTint();
      this.syncTransform();
      return result;
    }

    this.facing = this.computeFacing(playerPos);

    switch (this.state) {
      case 'idle':
        this.updateIdle(dt);
        break;
      case 'telegraph1':
        this.updateTelegraph(dt, 1);
        break;
      case 'attack1':
        this.updateAttack1(dt, playerOnWreckDeck, playerSlideInvulnerable, result);
        break;
      case 'telegraph2':
        this.updateTelegraph(dt, 2);
        break;
      case 'attack2':
        this.updateAttack2(dt, playerPos, result);
        break;
      case 'attack2_wait':
        this.updateAttack2Wait(dt, hubcapInFlight);
        break;
      case 'exposed':
        this.updateExposed(dt);
        break;
    }

    this.applyMechanicTint();
    this.applyHitFlashTint();
    this.syncTransform();
    return result;
  }

  private updateIdle(dt: number): void {
    if (this.hitFlashClipT <= 0) this.playClip('idle', true);
    this.stateT -= dt;
    if (this.stateT <= 0) {
      if (this.tryArmEnrage()) {
        this.enrageActive = true;
        this.state = 'telegraph1';
        this.stateT = this.telegraphDuration();
        this.playClip('telegraph1', false);
      } else {
        this.state = this.nextAttack === 1 ? 'telegraph1' : 'telegraph2';
        this.stateT = this.telegraphDuration();
        this.playClip(this.nextAttack === 1 ? 'telegraph1' : 'telegraph2', false);
      }
    }
  }

  /** K-1(b): true if the NEXT opener should be the heavy pocket-wide enrage instead of the normal telegraph1/telegraph2 alternation — PLAN-CITY3 §4's own literal spec (3 consecutive un-broken cycles, HP in the 60-70% band). */
  private tryArmEnrage(): boolean {
    if (this.consecutiveUnbrokenCycles < ENRAGE_ARM_CYCLES) return false;
    const hpFrac = this.health.hp / this.health.maxHp;
    return hpFrac >= ENRAGE_HP_BAND_MIN && hpFrac <= ENRAGE_HP_BAND_MAX;
  }

  private updateTelegraph(dt: number, which: 1 | 2): void {
    this.stateT -= dt;
    if (this.stateT <= 0) {
      this.state = which === 1 ? 'attack1' : 'attack2';
      this.stateT = 0;
      this.hasFiredThisAttack = false;
      this.currentAttackBroken = false;
      this.playClip(which === 1 ? 'attack1' : 'attack2', false);
    }
  }

  /**
   * K-1(a): the on-deck/slide check runs SYNCHRONOUSLY at the exact impact
   * tick — no wait-state needed here (unlike attack2's hubcap, this attack
   * has no travel time once it lands), which is why `reportShockwaveBroken`
   * is safe to call from main.ts in direct response to `shockwaveFired`
   * within the SAME tick: the exit transition below only runs once
   * `stateT >= attackDuration(1)`, a strictly LATER tick.
   */
  private updateAttack1(dt: number, playerOnWreckDeck: boolean, playerSlideInvulnerable: boolean, result: BossTickResult): void {
    this.stateT += dt;
    if (!this.hasFiredThisAttack && this.stateT >= ATTACK1_IMPACT_T) {
      this.hasFiredThisAttack = true;
      result.shockwaveFired = true;
      if (this.enrageActive) {
        result.shockwaveRadius = ENRAGE_SHOCKWAVE_RADIUS;
        // The LETHAL tier is selected the instant this WOULD be the second
        // consecutive un-answered enrage — chosen here, at the impact tick,
        // regardless of whether it ends up broken (a broken hit deals no
        // damage either way, via the on-deck/slide check main.ts applies
        // when resolving `shockwaveDamage` against the player).
        result.shockwaveDamage = this.unansweredEnrageStreak >= 1 ? ENRAGE_SHOCKWAVE_DAMAGE_LETHAL : ENRAGE_SHOCKWAVE_DAMAGE_FIRST;
      } else {
        result.shockwaveRadius = SHOCKWAVE_RADIUS;
        result.shockwaveDamage = SHOCKWAVE_DAMAGE;
      }
      if (playerOnWreckDeck || playerSlideInvulnerable) this.currentAttackBroken = true;
    }
    if (this.stateT >= this.attackDuration(1)) {
      if (this.enrageActive) {
        this.resolveEnrageOutcome();
      } else if (this.currentAttackBroken) {
        this.consecutiveUnbrokenCycles = 0;
        this.enterExposed(2);
      } else {
        this.consecutiveUnbrokenCycles++;
        this.returnToIdleUnbroken(2);
      }
    }
  }

  private updateAttack2(dt: number, playerPos: THREE.Vector3, result: BossTickResult): void {
    this.stateT += dt;
    if (!this.hasFiredThisAttack && this.stateT >= ATTACK2_RELEASE_T) {
      this.hasFiredThisAttack = true;
      // Fire-time-capture (§0.7): the target point is captured HERE, at the
      // exact tick the hubcap releases — main.ts's `computeLobVelocity` then
      // solves a fixed ballistic arc toward this frozen point, exactly like
      // the trash-can lobber's own `targetPos.clone()` pattern (ai/enemy.ts).
      result.projectileToFire = { origin: this.position.clone(), targetPos: playerPos.clone() };
    }
    if (this.stateT >= this.attackDuration(2)) {
      // K-1(a): the clip has finished, but the hubcap itself may still be
      // in flight (or, if fired at t=0 into a very short clip, may have
      // already resolved) — hold in attack2_wait until main.ts reports the
      // outcome via `hubcapInFlight` going false, rather than deciding
      // broken/unbroken here off stale information.
      this.state = 'attack2_wait';
      this.stateT = 0;
    }
  }

  private updateAttack2Wait(dt: number, hubcapInFlight: boolean): void {
    if (this.hitFlashClipT <= 0) this.playClip('idle', true); // no dedicated "waiting on the hubcap" clip exists — idle's own loop plays underneath, same idiom RatKingBoss's swarm_wait uses
    this.stateT += dt;
    if (!hubcapInFlight || this.stateT >= ATTACK2_WAIT_MAX) {
      if (this.currentAttackBroken) {
        this.consecutiveUnbrokenCycles = 0;
        this.enterExposed(1);
      } else {
        this.consecutiveUnbrokenCycles++;
        this.returnToIdleUnbroken(1);
      }
    }
  }

  /** K-1(a): a cycle that resolved un-broken gets NO reward — straight back to idle, no gold tint, no damage bonus. `next` is which opener the NEXT cycle should try. */
  private returnToIdleUnbroken(next: 1 | 2): void {
    this.state = 'idle';
    this.stateT = ENGAGE_DELAY;
    this.nextAttack = next;
    this.currentAttackBroken = false;
    if (this.hitFlashClipT <= 0) this.playClip('idle', true);
  }

  /** K-1(b): the enrage's own exit — broken resets the whole escalation and rewards EXPOSED exactly like a normal broken shockwave; un-broken advances the enrage streak (the damage itself was already resolved at the impact tick via `shockwaveDamage`'s tier choice). */
  private resolveEnrageOutcome(): void {
    this.enrageActive = false;
    this.consecutiveUnbrokenCycles = 0;
    if (this.currentAttackBroken) {
      this.unansweredEnrageStreak = 0;
      this.enterExposed(2);
    } else {
      this.unansweredEnrageStreak++;
      this.returnToIdleUnbroken(2);
    }
  }

  private enterExposed(followingAttack: 1 | 2): void {
    this.state = 'exposed';
    this.stateT = EXPOSED_DURATION;
    this.nextAttack = followingAttack;
    if (this.hitFlashClipT <= 0) this.playExposedClip();
  }

  private updateExposed(dt: number): void {
    if (this.hitFlashClipT <= 0) this.playExposedClip();
    this.stateT -= dt;
    if (this.stateT <= 0) {
      this.state = 'idle';
      this.stateT = ENGAGE_DELAY;
    }
  }

  /**
   * C08 (Wave M): a dedicated sagging/dazed loop with the signal-head
   * drooping and flickering off-rhythm — the "unload now" punish-window body
   * language the boss previously lacked entirely (it used to loop `idle`
   * under the gold tint, which read as "still alert," not "dazed"). Falls
   * back to `idle` automatically if the loaded GLB predates C08 and has no
   * `special_stagger` clip, so this is safe against a stale cached asset.
   */
  private playExposedClip(): void {
    if (this.visual.clips?.has('special_stagger')) this.playClip('special_stagger', true);
    else this.playClip('idle', true);
  }

  /** K-1(b): the enrage always reuses telegraph1's own clip/timing regardless of `nextAttack` — it is a third, orthogonal cycle, not a branch of the 1/2 alternation. */
  private telegraphDuration(): number {
    const name = this.enrageActive ? 'telegraph1' : this.nextAttack === 1 ? 'telegraph1' : 'telegraph2';
    const clip = this.visual.clips?.get(name);
    return clip?.duration ?? 0.65; // fallback only reachable if the GLB somehow loaded without this clip — validate-glb.mjs already guarantees it's present
  }

  private attackDuration(which: 1 | 2): number {
    const clip = this.visual.clips?.get(which === 1 ? 'attack1' : 'attack2');
    return clip?.duration ?? 0.65;
  }

  private computeFacing(playerPos: THREE.Vector3): number {
    const dx = playerPos.x - this.position.x;
    const dz = playerPos.z - this.position.z;
    const len = Math.hypot(dx, dz);
    return len > 1e-4 ? Math.atan2(dx / len, dz / len) : this.facing;
  }

  /**
   * K-1(b): the two mutually-exclusive mechanic tints — RED during the
   * enrage's own telegraph1/attack1 (a threat cue, same idiom RatKingBoss's
   * RED CHARGE already established), GOLD during the standard EXPOSED punish
   * window, or neither. Same `color===null` explicit-restore shape
   * `RatKingBoss.applyMechanicTint` uses, replacing the old binary
   * `applyExposedTint`'s always-gold-or-nothing lerp now that there are two
   * distinct non-exposed tints to choose between.
   */
  private applyMechanicTint(): void {
    if (this.health.flashT > 0) return; // a fresh hit's white flash (below) always wins over either mechanic tint
    const color = this.isEnraging ? RED : this.isExposed ? GOLD : null;
    for (const [m, base] of this.baseEmissive) {
      if (color) m.emissive.copy(base).lerp(color, 0.6); // 0.6, not 1 — a full-strength tint would wash out the lens colors C05's NOTES.md specifically preserved
      else m.emissive.copy(base);
    }
  }

  /** Same white hit-flash every enemy gets (`combat/damage.ts`'s `flashT`), layered on top of/instead of the mechanic tint above while active. */
  private applyHitFlashTint(): void {
    if (this.health.flashT <= 0) return;
    const lit = this.health.flashT / FLASH_DURATION;
    for (const [m, base] of this.baseEmissive) {
      m.emissive.copy(base).lerp(WHITE, lit);
    }
  }

  /**
   * Review finding: `takeDamage` has always computed and stored a real,
   * per-source knockback impulse into `health.knockbackVel` (via the shared
   * `applyDamage`) — bullets/melee/super each pass their own tuned force —
   * but nothing ever READ that field to actually move the boss, unlike
   * `Enemy.applyKnockbackMovement` (ai/enemy.ts), which every roster enemy
   * gets automatically. Mirrors that method exactly: resolve the decaying
   * velocity against walls via `physics.resolveHorizontal`, using
   * BOSS_RADIUS/BOSS_HEIGHT in place of an archetype's own values (a boss
   * has no `EnemyArchetype`). No ground-snap call — unlike roster enemies,
   * Gridlock's arena is flat street-level ground with no ramps to fall off.
   *
   * K-1(c) "physicality": the displacement actually applied each tick is
   * scaled down by GRIDLOCK_KNOCKBACK_RESIST (the underlying `knockbackVel`
   * itself is left untouched, so it still decays at `tickHealth`'s own
   * normal rate — only how much of it moves the boss is reduced), and the
   * result is hard-clamped to GRIDLOCK_HOME_LEASH from the boss's own home
   * point every tick, closing "sustained fire pushes it into its own fence"
   * from both ends: less drift per hit, and a hard ceiling on total drift
   * regardless of how many hits land before the resist decays it away.
   */
  private applyKnockbackMovement(dt: number, physics: BlockPhysics): void {
    if (this.health.knockbackVel.lengthSq() < 1e-4) return;
    const dx = this.health.knockbackVel.x * dt * (1 - GRIDLOCK_KNOCKBACK_RESIST);
    const dz = this.health.knockbackVel.z * dt * (1 - GRIDLOCK_KNOCKBACK_RESIST);
    const resolved = physics.resolveHorizontal(this.position.x, this.position.z, dx, dz, BOSS_RADIUS, this.position.y, this.position.y + BOSS_HEIGHT);
    this.position.x = resolved.x;
    this.position.z = resolved.z;
    const dxHome = this.position.x - this.homeX;
    const dzHome = this.position.z - this.homeZ;
    const distHome = Math.hypot(dxHome, dzHome);
    if (distHome > GRIDLOCK_HOME_LEASH) {
      const scale = GRIDLOCK_HOME_LEASH / distHome;
      this.position.x = this.homeX + dxHome * scale;
      this.position.z = this.homeZ + dzHome * scale;
    }
  }

  private syncTransform(): void {
    this.group.position.set(this.position.x, this.position.y, this.position.z);
    // R-1 (PLAN-CITY3 §0 L2): bos_gridlock.glb IS +X-forward too — rev.1 of
    // the plan guessed it might be Z-forward from bbox proportions (deeper
    // in Z than X); re-measured (arm mirror pairs across Z at ±0.66..1.16,
    // chest plate on the +X face) and confirmed false. Do not special-case
    // Gridlock. See core/assets.ts's CHARACTER_FORWARD_YAW_OFFSET.
    this.group.rotation.y = this.facing + CHARACTER_FORWARD_YAW_OFFSET;
  }
}
